// Results page animations and interactions

import { queryOptional } from "../utils/dom";

// Easing function: easeOutQuart for satisfying deceleration
const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

// Animate a number from start to end
function animateNumber(
  element: HTMLElement,
  start: number,
  end: number,
  duration: number,
  easing: (t: number) => number,
  prefix: string = "",
  suffix: string = "",
): Promise<void> {
  return new Promise((resolve) => {
    const startTime = performance.now();

    function update(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);
      const currentValue = Math.round(start + (end - start) * easedProgress);

      element.textContent = `${prefix}${currentValue}${suffix}`;

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(update);
  });
}

// Animate score numerator (The Verdict)
function animateScore(): void {
  const numeratorElement = queryOptional<HTMLElement>(".score-numerator");
  if (!numeratorElement) return;

  const targetScore = parseInt(numeratorElement.dataset.target || "0", 10);

  // Delay start to match CSS animation
  setTimeout(() => {
    animateNumber(numeratorElement, 0, targetScore, 1000, easeOutQuart);
  }, 300);
}

function createXpParticles(
  container: HTMLElement,
  targetElement: HTMLElement,
  count: number,
): void {
  const containerRect = container.getBoundingClientRect();
  const targetRect = targetElement.getBoundingClientRect();

  const centerX = targetRect.left - containerRect.left + targetRect.width / 2;
  const centerY = targetRect.top - containerRect.top + targetRect.height / 2;

  for (let i = 0; i < count; i++) {
    const particle = document.createElement("div");
    particle.className = "xp-particle";
    particle.style.left = `${centerX}px`;
    particle.style.top = `${centerY}px`;
    particle.style.setProperty("--tx", `${(Math.random() - 0.5) * 200}px`);
    particle.style.setProperty("--ty", `${(Math.random() - 0.5) * 200}px`);
    particle.style.animationDelay = `${Math.random() * 0.2}s`;
    container.appendChild(particle);
    setTimeout(() => particle.remove(), 1000);
  }
}

function animateXpValue(): void {
  const xpValueElement = queryOptional<HTMLElement>(".xp-value");
  const xpCard = queryOptional<HTMLElement>(".xp-card");
  if (!xpValueElement) return;

  const targetXp = parseInt(xpValueElement.dataset.target || "0", 10);

  setTimeout(() => {
    animateNumber(xpValueElement, 0, targetXp, 1500, easeOutQuart).then(() => {
      if (xpCard && targetXp > 0) {
        createXpParticles(xpCard, xpValueElement, 15);
      }
    });
  }, 1000);
}

function animateXpText(
  xpTextElement: HTMLElement,
  startXp: number,
  endXp: number,
  duration: number,
): void {
  const startTime = performance.now();

  function update(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutQuart(progress);

    const currentXp = Math.round(startXp + (endXp - startXp) * easedProgress);
    xpTextElement.textContent = currentXp.toString();

    if (progress < 1) {
      requestAnimationFrame(update);
    }
  }

  requestAnimationFrame(update);
}

function updateXpDisplay(xpInLevel: number, xpForLevel: number): void {
  const xpTextContainer = document.querySelector(
    ".level-bar-container .text-slate-500.font-mono",
  );
  if (xpTextContainer) {
    xpTextContainer.innerHTML = `<span class="xp-current">${xpInLevel}</span> / ${xpForLevel} XP`;
  }
}

function animateLevelBar(
  isLevelUp: boolean,
  newLevel: number,
  xpInNewLevel: number,
  xpForNewLevel: number,
  xpTargetInLevel: number,
  _xpTargetForLevel: number,
): void {
  const progressGainElement = queryOptional<HTMLElement>(
    ".level-progress-gain",
  );
  const xpTextElement = queryOptional<HTMLElement>(".xp-current");
  const progressBarContainer = queryOptional<HTMLElement>(
    ".level-bar-container",
  );

  if (!progressGainElement || !xpTextElement) return;

  const startWidth = parseFloat(progressGainElement.dataset.startWidth || "0");
  const targetWidth = parseFloat(
    progressGainElement.dataset.targetWidth || "0",
  );
  const startXp = parseInt(xpTextElement.textContent || "0", 10);

  // Set initial width before fade-in so bar appears at correct position
  progressGainElement.style.width = `${startWidth}%`;

  // Delay fade-in to appear after Elo/XP cards (which animate at 800ms and 1000ms)
  setTimeout(() => {
    // First, fade in the container
    if (progressBarContainer) {
      progressBarContainer.classList.add("visible");
    }

    // Wait for fade-in to complete (500ms), then start progress animation
    setTimeout(() => {
      if (isLevelUp) {
        // Level up animation sequence:
        // 1. Animate from start to 100% (filling old level)
        // 2. Trigger celebration
        // 3. Reset to 0%
        // 4. Animate from 0% to final position in new level

        // Force reflow to ensure initial width is applied
        void progressGainElement.offsetWidth;

        // Now animate to 100%
        progressGainElement.style.transition = "width 0.8s ease-out";
        if (progressBarContainer) {
          progressBarContainer.classList.add("near-level-up");
        }

        progressGainElement.style.width = "100%";

        // Animate XP text to fill the old level
        animateXpText(xpTextElement, startXp, xpTargetInLevel, 800);

        // Trigger celebration when bar reaches 100%
        setTimeout(() => {
          triggerLevelUpCelebration(newLevel);

          // Reset bar to 0% after celebration
          setTimeout(() => {
            // Update XP display to new level values
            updateXpDisplay(xpInNewLevel, xpForNewLevel);

            progressGainElement.style.transition = "none";
            progressGainElement.style.width = "0%";
            if (progressBarContainer) {
              progressBarContainer.classList.remove("near-level-up");
            }

            // Force reflow
            void progressGainElement.offsetWidth;

            // Phase 2: Animate from 0% to final position
            progressGainElement.style.transition = "width 0.6s ease-out";
            progressGainElement.style.width = `${targetWidth}%`;

            const newXpElement = queryOptional<HTMLElement>(".xp-current");
            if (newXpElement) {
              animateXpText(newXpElement, 0, xpInNewLevel, 600);
            }
          }, 200);
        }, 800);
      } else {
        // Normal animation: start -> target
        // Force reflow to ensure initial width is applied
        void progressGainElement.offsetWidth;

        // Now animate to target with transition
        progressGainElement.style.transition = "width 1.5s ease-out";
        progressGainElement.style.width = `${targetWidth}%`;

        // Animate XP text
        animateXpText(xpTextElement, startXp, xpTargetInLevel, 1500);
      }
    }, 500);
  }, 1200);
}

function createScreenFlash(): void {
  const flash = document.createElement("div");
  flash.className = "level-up-flash";
  document.body.appendChild(flash);
  setTimeout(() => flash.remove(), 800);
}

function createConfetti(count: number): void {
  const colors: string[] = [
    "#fbbf24",
    "#a855f7",
    "#3b82f6",
    "#10b981",
    "#ef4444",
  ];
  for (let i = 0; i < count; i++) {
    const confetti = document.createElement("div");
    confetti.className = "confetti";
    confetti.style.left = `${Math.random() * 100}vw`;
    confetti.style.top = "-10px";
    const colorIndex = Math.floor(Math.random() * colors.length);
    confetti.style.backgroundColor = colors[colorIndex] as string;
    confetti.style.width = `${Math.random() * 8 + 6}px`;
    confetti.style.height = `${Math.random() * 8 + 6}px`;
    confetti.style.borderRadius = Math.random() > 0.5 ? "50%" : "0";
    confetti.style.animationDuration = `${Math.random() * 2 + 2}s`;
    confetti.style.animationDelay = `${Math.random() * 0.5}s`;
    document.body.appendChild(confetti);
    setTimeout(() => confetti.remove(), 4000);
  }
}

function triggerLevelUpCelebration(newLevel: number): void {
  const levelBadge = queryOptional<HTMLElement>(".level-badge");
  const levelText = queryOptional<HTMLElement>(".level-text");
  const levelUpBanner = queryOptional<HTMLElement>("[data-level-up-banner]");

  createScreenFlash();
  createConfetti(30);

  if (levelBadge) {
    levelBadge.textContent = newLevel.toString();
    levelBadge.classList.add("level-up-badge");
  }

  if (levelText) {
    levelText.textContent = `Level ${newLevel}`;
  }

  if (levelUpBanner) {
    levelUpBanner.style.opacity = "1";
  }
}

// Animate Elo change badge
function animateEloBadge(): void {
  const eloCard = queryOptional<HTMLElement>(".elo-card");
  if (!eloCard) return;

  const badge = queryOptional<HTMLElement>(".elo-change-badge", eloCard);
  if (!badge) return;

  // Add pop animation after card entrance
  setTimeout(() => {
    badge.style.opacity = "1";
    badge.style.transform = "scale(1)";
  }, 1400);
}

// Initialize share button functionality
function initShareButton(): void {
  const shareBtn = document.getElementById("share-result-btn");
  if (!shareBtn) return;

  shareBtn.addEventListener("click", async () => {
    const testAttemptId = shareBtn.dataset.testAttemptId;
    if (!testAttemptId) return;

    try {
      // First, call the share endpoint to make the result public
      const response = await fetch(`/results/${testAttemptId}/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to share result");
      }

      const data = await response.json();
      const shareUrl = data.share_url;

      // Copy URL to clipboard
      await navigator.clipboard.writeText(shareUrl);
      showShareToast("Link copied! Result is now publicly viewable.");
    } catch {
      showShareToast("Failed to share result. Please try again.");
    }
  });
}

// Show toast notification for share
function showShareToast(message: string): void {
  const existingToast = document.querySelector(".share-toast");
  if (existingToast) existingToast.remove();

  const toast = document.createElement("div");
  toast.className = "share-toast";
  toast.innerHTML = `
    <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
    </svg>
    <span>${message}</span>
  `;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add("visible");
  }, 10);

  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

// Main initialization
export function initResults(): void {
  const resultsContainer = queryOptional<HTMLElement>(
    "[data-controller='results']",
  );
  if (!resultsContainer) return;

  const isLevelUp = resultsContainer.dataset.resultsLevelUp === "true";
  const newLevel = parseInt(
    resultsContainer.dataset.resultsNewLevel || "0",
    10,
  );
  const xpInNewLevel = parseInt(
    resultsContainer.dataset.resultsXpInNewLevel || "0",
    10,
  );
  const xpForNewLevel = parseInt(
    resultsContainer.dataset.resultsXpForNewLevel || "100",
    10,
  );
  const xpTargetInLevel = parseInt(
    resultsContainer.dataset.resultsXpTargetInLevel || "0",
    10,
  );
  const xpTargetForLevel = parseInt(
    resultsContainer.dataset.resultsXpTargetForLevel || "100",
    10,
  );

  animateScore();
  animateXpValue();
  animateLevelBar(
    isLevelUp,
    newLevel,
    xpInNewLevel,
    xpForNewLevel,
    xpTargetInLevel,
    xpTargetForLevel,
  );
  animateEloBadge();
  initShareButton();
}

// Auto-initialize on DOMContentLoaded (for non-Turbo pages)
document.addEventListener("DOMContentLoaded", initResults);
